import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { colorSchemeLightCold, themeQuartz, type ColDef } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy, Loader2, Pause, Play, RefreshCw, Trash2, X } from 'lucide-react';
import api from '@/api/client';
import { setQueryTraceRecording } from '@/lib/queryTrace';
import { useErrorToast } from '@/lib/notify';

type Query = { id: string; requestId: string; actionId: string; action: string; page: string; endpoint: string; method: string; at: string; durationMs: number; rowCount: number; status: string; error: string | null; query: string; params: unknown; planAvailable?: boolean };
type RequestTrace = { id: string; durationMs: number; statusCode: number; endpoint: string; method: string; action: string; at: string };
type Snapshot = { enabled: boolean; queries: Query[]; requests: RequestTrace[]; limit: number };
const url = '/monitoring/session-trace';
const ms = (value: number) => `${value.toLocaleString('tr-TR', { maximumFractionDigits: 2 })} ms`;

const modules = [AllEnterpriseModule];
const theme = themeQuartz.withPart(colorSchemeLightCold).withParams({
  fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11.5, rowHeight: 34,
  headerHeight: 34, borderRadius: 6, headerBackgroundColor: '#f8fafc',
  borderColor: '#e2e8f0', oddRowBackgroundColor: '#f8fafc',
});
const defaultColDef: ColDef = { sortable: true, filter: true, floatingFilter: false, resizable: true, minWidth: 110, flex: 1 };
const querySelection = { mode: 'singleRow' as const, checkboxes: false, enableClickSelection: true };
const durationStyle = ({ value }: { value: number | null }) => ({ color: value != null && value > 1000 ? '#e11d48' : '#334155' });
const numericColumn: Pick<ColDef, 'type' | 'cellDataType' | 'filter' | 'cellClass' | 'headerClass'> = {
  type: 'numericColumn', cellDataType: 'number', filter: 'agNumberColumnFilter',
  cellClass: 'ag-right-aligned-cell tabular-nums', headerClass: 'ag-right-aligned-header',
};
const timeColumn: ColDef = {
  field: 'at', headerName: 'Zaman', minWidth: 165, filter: 'agDateColumnFilter',
  valueFormatter: ({ value }) => value ? new Date(value).toLocaleString('tr-TR') : '—',
  filterParams: { comparator: (day: Date, value: string) => {
    const date = new Date(value); date.setHours(0, 0, 0, 0);
    return date.getTime() - day.getTime();
  } },
};
type QueryRow = Query & { apiDurationMs: number | null };

function CopyableSql({ sql }: { sql: string }) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  useErrorToast(error);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  async function copySql() {
    setError('');
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(sql);
      else {
        // Clipboard API is unavailable on HTTP LAN addresses used during local testing.
        const previous = document.activeElement as HTMLElement | null;
        const field = document.createElement('textarea');
        field.value = sql; field.setAttribute('readonly', '');
        field.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
        document.getElementById('session-query-trace-dialog')?.appendChild(field);
        try {
          field.select();
          if (!document.execCommand('copy')) throw new Error('Copy failed');
        } finally { field.remove(); previous?.focus({ preventScroll: true }); }
      }
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1800);
    } catch { setError('Sorgu kopyalanamadı. Tarayıcının pano iznini kontrol edin.'); }
  }
  return <div className="relative mt-1 overflow-hidden rounded-lg border border-slate-200 bg-white">
    <button type="button" onClick={() => void copySql()} aria-label={copied ? 'Sorgu kopyalandı' : 'Sorguyu kopyala'} title={copied ? 'Kopyalandı!' : 'Sorguyu kopyala'} className={`absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-md border shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${copied ? 'border-emerald-200 bg-emerald-50 text-emerald-600' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-100 hover:text-slate-800'}`}>
      {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
    </button>
    <span role="status" className="sr-only">{copied ? 'Sorgu panoya kopyalandı.' : ''}</span>
    <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all p-3 pr-14">{sql}</pre>
  </div>;
}

function QueryPlan({ queryId, available, autoLoad = false }: { queryId: string; available?: boolean; autoLoad?: boolean }) {
  const autoLoaded = useRef(false);
  const [plan, setPlan] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const loadPlan = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.post<{ plan: string }>(`${url}/plan`, { queryId });
      setPlan(data.plan);
    } catch { setError('Plan alınamadı. Kayıt silinmiş, parametreler eksik veya planlama süresi aşılmış olabilir.'); }
    finally { setLoading(false); }
  }, [queryId]);
  useEffect(() => {
    if (autoLoad && available && !autoLoaded.current) { autoLoaded.current = true; void loadPlan(); }
  }, [autoLoad, available, loadPlan]);
  return <div className="mt-3">
    <button type="button" disabled={!available || loading} onClick={() => void loadPlan()} title={available ? 'Sorguyu çalıştırmadan tahmini PostgreSQL planını göster' : 'Yalnızca tam metni ve parametreleri saklanmış SELECT / WITH sorguları desteklenir'} className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 px-3 py-2 font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-40">{loading && <Loader2 size={12} className="animate-spin" />}{plan ? 'Planı yenile' : 'Sorgu planını göster'}</button>
    {!available && <p className="mt-2 text-slate-500">{available === undefined ? 'Bu eski kayıtta plan bilgisi yok. Kayıt açıkken işlemi tekrar yapıp yeni sorguyu seçin.' : 'Bu kayıt için plan alınamıyor: sorgu SELECT / WITH türünde olmayabilir veya metni / parametreleri gizlenmiş ya da kısaltılmış olabilir.'}</p>}
    {error && <p role="alert" className="mt-2 text-rose-600">{error}</p>}
    {plan && <><p className="mt-2 text-[11px] text-slate-500">Tahmini plan · Sorgu çalıştırılmaz. Gerçek çalışma süresi ve buffer ölçümleri içermez.</p><pre className="mt-2 max-h-96 overflow-auto rounded-lg bg-slate-900 p-3 text-[11px] text-slate-100">{plan}</pre></>}
  </div>;
}

export default function SessionQueryTrace({ onClose }: { onClose: () => void }) {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [error, setError] = useState('');
  useErrorToast(error);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'queries' | 'requests'>('queries');
  const [selectedQuery, setSelectedQuery] = useState<Query | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const alive = useRef(true);
  const requestNumber = useRef(0);
  const mutating = useRef(false);
  const load = useCallback(async (force = false) => {
    if (mutating.current && !force) return;
    const request = ++requestNumber.current;
    try {
      const { data } = await api.get<Snapshot>(url);
      if (!alive.current || request !== requestNumber.current) return;
      setSnapshot(data); setQueryTraceRecording(data.enabled); setError('');
    } catch {
      if (alive.current && request === requestNumber.current) setError('Sorgu izleyiciye erişilemedi. Redis bağlantısını kontrol edip tekrar deneyin.');
    } finally { if (alive.current) setLoading(false); }
  }, []);
  useEffect(() => {
    alive.current = true;
    void load();
    const timer = window.setInterval(() => { if (!document.hidden) void load(); }, 3000);
    const previous = document.activeElement as HTMLElement | null;
    close.current?.focus();
    const keyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); }
      if (event.key !== 'Tab') return;
      const controls = Array.from(panel.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input, summary, [tabindex="0"]') ?? []);
      if (event.shiftKey && document.activeElement === controls[0]) { event.preventDefault(); controls.at(-1)?.focus(); }
      if (!event.shiftKey && document.activeElement === controls.at(-1)) { event.preventDefault(); controls[0]?.focus(); }
    };
    document.addEventListener('keydown', keyDown);
    return () => { alive.current = false; window.clearInterval(timer); document.removeEventListener('keydown', keyDown); previous?.focus(); };
  }, [load, onClose]);
  async function mutate(action: 'toggle' | 'clear') {
    if (!snapshot || busy) return;
    mutating.current = true;
    setBusy(true); ++requestNumber.current;
    try {
      if (action === 'clear') { await api.delete(url); setSelectedQuery(null); }
      else {
        const enabled = !snapshot.enabled;
        await api.put(url, { enabled });
        setQueryTraceRecording(enabled);
      }
      await load(true);
    } catch { setError('İşlem tamamlanamadı. Tekrar deneyin.'); }
    finally { mutating.current = false; if (alive.current) setBusy(false); }
  }
  const queries = useMemo<QueryRow[]>(() => {
    const requestTimes = new Map(snapshot?.requests.map(request => [request.id, request.durationMs]));
    return snapshot?.queries.map(query => ({ ...query, apiDurationMs: requestTimes.get(query.requestId) ?? null })) ?? [];
  }, [snapshot]);
  const queryColumns = useMemo<ColDef<QueryRow>[]>(() => [
    timeColumn,
    { field: 'action', headerName: 'İşlem', minWidth: 180 },
    { field: 'page', headerName: 'Ekran', minWidth: 180 },
    { field: 'method', headerName: 'Metot', minWidth: 90 },
    { field: 'endpoint', headerName: 'API', minWidth: 230, tooltipField: 'endpoint' },
    { ...numericColumn, field: 'apiDurationMs', headerName: 'API süresi (ms)', minWidth: 155, valueFormatter: ({ value }) => value == null ? '—' : ms(value), cellStyle: durationStyle },
    { ...numericColumn, field: 'durationMs', headerName: 'Sorgu süresi (ms)', minWidth: 165, valueFormatter: ({ value }) => ms(value), cellStyle: durationStyle },
    { ...numericColumn, field: 'rowCount', headerName: 'Satır', minWidth: 110, valueFormatter: ({ value }) => Number(value ?? 0).toLocaleString('tr-TR') },
    { field: 'status', headerName: 'Sonuç', minWidth: 120, filter: 'agSetColumnFilter', filterParams: { valueFormatter: ({ value }: { value: string }) => value === 'error' ? 'Hatalı' : 'Başarılı' }, valueFormatter: ({ value }) => value === 'error' ? 'Hatalı' : 'Başarılı', cellStyle: ({ value }) => ({ color: value === 'error' ? '#e11d48' : '#047857' }) },
    { field: 'query', headerName: 'SQL', minWidth: 280, tooltipField: 'query' },
    { field: 'requestId', headerName: 'İstek kimliği', minWidth: 250, hide: true },
  ], []);
  const requestColumns = useMemo<ColDef<RequestTrace>[]>(() => [
    timeColumn,
    { field: 'action', headerName: 'İşlem', minWidth: 180 },
    { field: 'method', headerName: 'Metot', minWidth: 90 },
    { field: 'endpoint', headerName: 'API', minWidth: 280, tooltipField: 'endpoint' },
    { ...numericColumn, field: 'durationMs', headerName: 'Süre (ms)', minWidth: 150, valueFormatter: ({ value }) => ms(value), cellStyle: durationStyle },
    { ...numericColumn, field: 'statusCode', headerName: 'HTTP', minWidth: 110, cellStyle: ({ value }) => ({ color: value >= 400 ? '#e11d48' : '#047857' }) },
  ], []);
  const limit = snapshot?.limit ?? 1000;
  return createPortal(<div data-query-trace-panel className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/40 p-2 sm:p-5" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
    <div ref={panel} id="session-query-trace-dialog" role="dialog" aria-modal="true" aria-labelledby="session-query-trace-title" className="relative flex h-[92dvh] w-full max-w-7xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
      <header className="flex shrink-0 items-center justify-between gap-3 border-b p-4">
        <div><h2 id="session-query-trace-title" className="text-lg font-bold text-slate-900">Oturum sorgu izleyici</h2><p className="mt-1 text-xs text-slate-500">Yalnızca bu oturum · Son {limit} sorgu ve API isteği · 30 dakika hareketsizlikte kayıt durur</p></div>
        <button ref={close} type="button" aria-label="İzleme ekranını kapat" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><X size={20} /></button>
      </header>
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b p-3 text-xs">
        <span className={`rounded-full px-2 py-1 font-semibold ${snapshot?.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{snapshot?.enabled ? 'Kayıt açık' : 'Kayıt kapalı'}</span>
        <button type="button" disabled={busy || !snapshot} onClick={() => void mutate('toggle')} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-3 font-semibold text-white disabled:opacity-50">{snapshot?.enabled ? <Pause size={14} /> : <Play size={14} />}{snapshot?.enabled ? 'Kaydı durdur' : 'Kaydı başlat'}</button>
        <button type="button" disabled={busy || !snapshot} onClick={() => void mutate('clear')} className="inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 disabled:opacity-50"><Trash2 size={14} />Temizle</button>
        <button type="button" disabled={busy} onClick={() => void load()} className="inline-flex h-9 items-center gap-1.5 rounded-lg border px-3"><RefreshCw size={14} />Yenile</button>
      </div>
      <div className="flex shrink-0 gap-2 px-3 pt-3 text-xs">
        <button type="button" aria-pressed={tab === 'queries'} onClick={() => setTab('queries')} className={`rounded-lg px-3 py-2 ${tab === 'queries' ? 'bg-blue-50 font-bold text-blue-700' : 'text-slate-500'}`}>Sorgular ({snapshot?.queries.length ?? 0}/{limit})</button>
        <button type="button" aria-pressed={tab === 'requests'} onClick={() => setTab('requests')} className={`rounded-lg px-3 py-2 ${tab === 'requests' ? 'bg-blue-50 font-bold text-blue-700' : 'text-slate-500'}`}>API istekleri ({snapshot?.requests.length ?? 0}/{limit})</button>
      </div>
      <div className="min-h-0 flex-1 p-3">
        <div className={tab === 'queries' ? 'h-full' : 'hidden'}>
          <AgGridReact<QueryRow> modules={modules} theme={theme} rowData={queries} columnDefs={queryColumns} defaultColDef={defaultColDef}
            rowSelection={querySelection} onSelectionChanged={event => { const row = event.api.getSelectedRows()[0]; if (row) setSelectedQuery(row); }}
            getRowId={({ data }) => data.id} loading={loading} enableCellTextSelection suppressScrollOnNewData
            popupParent={panel.current ?? undefined} overlayNoRowsTemplate="<span>Henüz kayıt yok veya filtreye uygun sorgu bulunamadı.</span>" />
        </div>
        <div className={tab === 'requests' ? 'h-full' : 'hidden'}>
          <AgGridReact<RequestTrace> modules={modules} theme={theme} rowData={snapshot?.requests ?? []} columnDefs={requestColumns} defaultColDef={defaultColDef}
            getRowId={({ data }) => data.id} loading={loading} enableCellTextSelection suppressScrollOnNewData
            popupParent={panel.current ?? undefined} overlayNoRowsTemplate="<span>Henüz kayıt yok veya filtreye uygun API isteği bulunamadı.</span>" />
        </div>
      </div>
      {tab === 'queries' && selectedQuery && <section className="max-h-[45%] shrink-0 overflow-auto border-t bg-slate-50 p-4 text-xs" aria-label="Sorgu detayı">
        <div className="flex items-center justify-between gap-3"><h3 className="font-bold text-slate-800">Sorgu detayı · {selectedQuery.method} {selectedQuery.endpoint}</h3><button type="button" aria-label="Sorgu detayını kapat" onClick={() => setSelectedQuery(null)} className="rounded p-1 hover:bg-slate-200"><X size={16} /></button></div>
        {selectedQuery.error && <p className="mt-2 text-rose-600">{selectedQuery.error}</p>}
        <div className="mt-2 grid gap-3 md:grid-cols-2"><div><h4 className="font-semibold text-slate-500">SQL</h4><CopyableSql key={selectedQuery.id} sql={selectedQuery.query} /></div><div><h4 className="font-semibold text-slate-500">Parametreler</h4><pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-white p-3">{JSON.stringify(selectedQuery.params, null, 2)}</pre></div></div>
        <QueryPlan key={selectedQuery.id} queryId={selectedQuery.id} available={selectedQuery.planAvailable} autoLoad />
      </section>}
      <p className="shrink-0 border-t px-4 py-3 text-xs text-slate-500">Ekranı kapatmak kaydı durdurmaz. Hassas alanlar maskelenir; sorgular yeniden çalıştırılmaz. Tıklama etiketi, ardından 10 saniye içindeki istekler için kullanılır.</p>
    </div>
  </div>, document.body);
}
